import { useRootStore } from 'stores/utils/hooks/useRootStore'
import { getAgeGroupFromAge } from 'utils/userState'
import { AgeGroup } from 'constants/components'
import {
  CAT_4784_PREMIUM_AMOUNT_THRESHOLD,
  isPremiumCustomer
} from 'utils/userState'
import { getAge } from 'utils/date'
import { getRceCustomerCategory } from 'utils/getRceCustomerCategory'
import { isCat4784SmavaDacSkipButtonVarB } from 'gates/helpers/cat4784SmavaDacSkipButton'
import { isTenantSmava } from 'utils/url'
import type RootStore from 'stores/RootStore'

export type UserState = {
  isPremium: boolean
  isYoungAge: boolean
  isMiddleAge: boolean
  isOldAge: boolean
  isSalableCustomer: boolean
  isAcceptedCustomer: boolean
  isAbortedCustomer: boolean
  isAbortedDacCompleteCustomer: boolean
  ageGroup: AgeGroup
  debtorsAge: number
  debtorsCount: number
}

export type UserStateMatchers = {
  isAnyOf: (...flags: UserStateFlag[]) => boolean
  isAllOf: (...flags: UserStateFlag[]) => boolean
  isNoneOf: (...flags: UserStateFlag[]) => boolean
}

// The boolean segment flags of UserState — the only fields matchers below can combine.
export type UserStateFlag = {
  [Key in keyof UserState]: UserState[Key] extends boolean ? Key : never
}[keyof UserState]

// True when at least one of the given flags is set, e.g. isAnyOf(state, 'isPremium', 'isOldAge')
export const isAnyOf = (state: UserState, ...flags: UserStateFlag[]): boolean =>
  flags.some((flag) => state[flag])

// True when all of the given flags are set
export const isAllOf = (state: UserState, ...flags: UserStateFlag[]): boolean =>
  flags.every((flag) => state[flag])

// True when none of the given flags are set, e.g. isNoneOf(state, 'isMiddleAge', 'isPremium')
export const isNoneOf = (
  state: UserState,
  ...flags: UserStateFlag[]
): boolean => !isAnyOf(state, ...flags)

// Pure function — for config-level and test usage
export function getUserState(
  rootStore: RootStore,
  isSmava = isTenantSmava()
): UserState {
  const { traversal } = rootStore
  const birthday = traversal.data?.debtors?.primary?.personal?.birthday ?? ''
  const amount = traversal.data?.common?.amount ?? 0
  const primaryIncome = traversal.data?.debtors?.primary?.income?.primary ?? 0
  const debtorsAge = birthday ? getAge(birthday) : 0
  const ageGroup = getAgeGroupFromAge(debtorsAge)
  const isCat4784Experiment = isCat4784SmavaDacSkipButtonVarB({
    experimentData: rootStore.experimentData ?? {},
    tenant: isSmava ? 'smava' : 'ffg'
  })

  return {
    isPremium: isPremiumCustomer({
      debtorsAge,
      amount,
      income: { primary: primaryIncome },
      amountThreshold: isCat4784Experiment
        ? CAT_4784_PREMIUM_AMOUNT_THRESHOLD
        : undefined
    }),
    isYoungAge: ageGroup === AgeGroup.YOUNG_AGE_GROUP,
    isMiddleAge: ageGroup === AgeGroup.MIDDLE_AGE_GROUP,
    isOldAge: ageGroup === AgeGroup.OLD_AGE_GROUP,
    ...getRceCustomerCategory({
      utm_campaign: rootStore.urlParams.utm_campaign,
      parentUrl: rootStore.navigation?.parentUrl
    }),
    ageGroup,
    debtorsAge,
    debtorsCount: traversal.data?.debtors?.count ?? 1
  }
}

// Reactive hook — MobX tracks accessed observables via observer()
function useUserState(): UserState & UserStateMatchers {
  const { rootStore } = useRootStore()
  const state = getUserState(rootStore)

  return {
    ...state,
    isAnyOf: (...flags) => isAnyOf(state, ...flags),
    isAllOf: (...flags) => isAllOf(state, ...flags),
    isNoneOf: (...flags) => isNoneOf(state, ...flags)
  }
}

export default useUserState
